<template>
  <v-dialog v-model="model" fullscreen hide-overlay transition="dialog-bottom-transition" :retain-focus="false">
    <div class="rd gm-month" :class="{ 'ui-compact': device.isMobile }">
      <div class="gm-month__bar">
        <button type="button" class="ui-icon-btn" :aria-label="$t('close')" @click="model = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
        <div class="gm-month__title">
          <h2>{{ $t("tooltip_monthly_report") }}</h2>
          <span class="rd-muted">{{ firmName }}</span>
        </div>
        <span v-if="copyNote" class="ui-chip ui-chip--ok" role="status">{{ copyNote }}</span>
        <button type="button" class="ui-btn ui-btn--sm" :disabled="!rows.length" @click="copyTable">
          <v-icon size="16">mdi-content-copy</v-icon>
          <span class="gm-month__label">{{ $t("report_desktop.copy_table") }}</span>
        </button>
        <button type="button" class="ui-btn ui-btn--sm" :disabled="!rows.length" @click="printReport">
          <v-icon size="16">mdi-printer-outline</v-icon>
          <span class="gm-month__label">{{ $t("report_mobile.print_pdf") }}</span>
        </button>
        <button type="button" class="ui-btn ui-btn--sm" :disabled="!rows.length" @click="exportReportToExcel">
          <v-icon size="16">mdi-microsoft-excel</v-icon>
          <span class="gm-month__label">{{ $t("report_mobile.download_excel") }}</span>
        </button>
      </div>

      <div class="gm-month__body">
        <div class="gm-month__stats">
          <div class="ui-stat ui-stat--inverse">
            <span class="ui-stat__label">{{ $t("ordered") }}</span>
            <span class="ui-stat__value">{{ totals.total.quantity }}</span>
          </div>
          <template v-if="canSeeTaken">
            <div class="ui-stat">
              <span class="ui-stat__label">{{ $t("taken") }}</span>
              <span class="ui-stat__value">{{ totals.total.payed }}</span>
            </div>
            <div class="ui-stat">
              <span class="ui-stat__label">{{ $t("difference") }}</span>
              <span class="ui-stat__value">{{ totals.total.quantity - totals.total.payed }}</span>
            </div>
          </template>
        </div>

        <div class="gm-month__tools">
          <div class="rd-month-nav gm-month__nav">
            <button
              type="button"
              class="ui-icon-btn ui-icon-btn--sm"
              :aria-label="$t('report_mobile.prev_month')"
              @click="stepMonth(-1)"
            >
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <div class="rd-month-nav__title" aria-live="polite">{{ monthName }}</div>
            <button
              type="button"
              class="ui-icon-btn ui-icon-btn--sm"
              :aria-label="$t('report_mobile.month_next')"
              @click="stepMonth(1)"
            >
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <ui-segment v-model="type" tabs :options="typeOptions" :label="$t('view')" />

          <div class="ui-chips">
            <ui-toggle :pressed="ordered_meals" @click="ordered_meals = !ordered_meals">{{ $t("ordered_1") }}</ui-toggle>
            <ui-toggle v-if="canSeeTaken" :pressed="taken_meals" @click="taken_meals = !taken_meals">
              {{ $t("taken") }}
            </ui-toggle>
          </div>

          <label class="rd-live">
            <input v-model="allDayFirm" type="checkbox" class="rd-switch" role="switch" />
            {{ $t("show_all_days") }}
          </label>
        </div>

        <report-state v-if="loading" kind="loading"></report-state>
        <report-state
          v-else-if="!rows.length"
          icon="mdi-calendar-blank-outline"
          :title="$t('no_data_available')"
        ></report-state>

        <ui-table
          v-else
          id="monthlyOrdersTable"
          variant="report"
          wrap-class="rd-card--scroll"
          :columns="columns"
          :rows="rows"
          :row-key="(row) => row.key"
          :totals="footTotals"
          :total-label="$t('total')"
        >
          <template #head-shift="{ column }">
            <report-shift-tag :shift="column.shift.id"></report-shift-tag>
          </template>

          <template #cell-shift="{ row, column }">
            <template v-if="row.cells[column.key].empty">
              <span class="rd-dash" aria-hidden="true">–</span>
              <span class="rd-visually-hidden">0</span>
            </template>
            <template v-else>{{ row.cells[column.key].text }}</template>
          </template>

          <template #cell-total="{ row }">
            <b>{{ row.cells.total.text }}</b>
          </template>
        </ui-table>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { SHIFTS } from "@/constants/shifts";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import reportService from "@/services/report";
import hasTakenReport from "../taken/mixins/hasTakenReport";
import ReportState from "../../desktop/ReportState.vue";
import { copyText, rowsToTsv } from "../../desktop/copyTable";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import "../../desktop/reportDesktop.scss";

const MONTH = "YYYY-MM";

/**
 * Mesečni pregled firme (jela iz grupa) preko celog ekrana, u izgledu izveštaja: brojke, izbor meseca, prikaz
 * "Narudžbe / Obroci / Jela", naručeno i preuzeto po danu i smeni. "Narudžbe" i "Obroci" čitaju gotove zbirove po danu
 * (food-orders-summary?group=orders&by_date=1), "Jela" stavke iz food-orders-ordered.
 */
export default {
  components: { ReportShiftTag, ReportState },
  mixins: [hasModel, hasExcel, hasPrinting, hasTakenReport],
  name: "ReportMealsTakenCompanyMonthlyOrder",
  props: {
    firmReportItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      excelLoading: false,
      date1: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 7), // YYYY-MM format
      allDayFirm: false,
      taken_meals: false,
      ordered_meals: true,
      type: "meals",
      summaryItems: [],
      rawFirmDataByDate: {},
      firmReportItemInternal: {},
      copyNote: "",
    };
  },
  methods: {
    formatShiftValue(shiftData) {
      if (this.ordered_meals && this.taken_meals) {
        return [shiftData.quantity, shiftData.payed].join("/");
      }
      if (this.ordered_meals) return shiftData.quantity;
      if (this.taken_meals) return shiftData.payed;
      return "";
    },
    /** Ćelija tabele na ekranu: tekst ("naručeno / preuzeto", samo jedno, ili prazno) i da li je sve nula. */
    cellOf(shiftData) {
      const { quantity, payed } = shiftData;
      let text = "";
      if (this.ordered_meals && this.taken_meals) {
        text = `${quantity} / ${payed}`;
      } else if (this.ordered_meals) {
        text = String(quantity);
      } else if (this.taken_meals) {
        text = String(payed);
      }
      return { text, empty: !text || (!quantity && !payed) };
    },
    formatRowForExport(item) {
      return {
        ...item,
        shift_one: this.formatShiftValue(item.shift_one),
        shift_two: this.formatShiftValue(item.shift_two),
        shift_three: this.formatShiftValue(item.shift_three),
        shift_four: this.formatShiftValue(item.shift_four),
        total: this.formatShiftValue(item.total),
        difference: this.ordered_meals && this.taken_meals ? item.difference : undefined,
      };
    },
    stepMonth(step) {
      this.date1 = moment(`${this.date1}-01`, "YYYY-MM-DD").add(step, "month").format(MONTH);
    },
    async getCompanyMonthlyOrders() {
      if (!this.firmReportItemInternal || !this.firmReportItemInternal.company_id) {
        this.rawFirmDataByDate = {};
        return;
      }
      this.loading = true;
      const year = this.year;
      const month = this.month;
      const companyId = this.firmReportItemInternal.company_id;

      // Get first and last day of the month
      const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDayDate = new Date(year, month, 0);
      const lastDay = `${year}-${String(month).padStart(2, "0")}-${String(
        lastDayDate.getDate()
      ).padStart(2, "0")}`;
      const params = { date: firstDay, date2: lastDay };
      if (this.type === "foods") {
        await this.fetchTakenReport(params, companyId);
      } else {
        await this.fetchMonthlySummary(params, companyId);
      }
      this.loading = false;
    },
    // "Narudžbe" i "Obroci": gotovi zbirovi po danu i smeni (food-orders-summary?group=orders&by_date=1)
    async fetchMonthlySummary(params, companyId) {
      const requestId = (this.summaryRequestId = (this.summaryRequestId || 0) + 1);
      try {
        const response = await reportService.getFoodOrdersSummary(this.$store.state.restaurant.id, {
          ...params,
          group: "orders",
          by_date: 1,
          companies: companyId,
        });
        if (requestId !== this.summaryRequestId) return;
        this.summaryItems = (response.data || []).filter((item) => item.company_id === companyId);
      } catch (error) {
        if (requestId !== this.summaryRequestId) return;
        console.error("Error fetching monthly summary:", error);
        this.summaryItems = [];
      }
    },
    async copyTable() {
      const headers = [
        this.$t("day"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
        ...(this.showDifference ? [this.$t("difference")] : []),
      ];
      const lines = this.rows.map((row) => [
        row.copyDay,
        ...this.visibleShifts.map((shift) => row.cells[shift.key].text),
        row.cells.total.text,
        ...(this.showDifference ? [row.difference] : []),
      ]);
      const copied = await copyText(rowsToTsv(headers, lines));
      this.copyNote = this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed");
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        this.copyNote = "";
      }, 2400);
    },
    async exportReportToExcel() {
      // Sanitize worksheet name to remove forbidden characters: * ? : \ / [ ]
      const sanitizeSheetName = (name) =>
        name
          .replace(/[*?:\\/\[\]]/g, " ")
          .trim()
          .substring(0, 31);

      this.downloadExcel({
        fileName: this.exportTitle + ".xlsx",
        sheets: [
          {
            name: sanitizeSheetName(this.exportTitle),
            headers: this.dynamicTableHeaders,
            items: this.exportItems,
            title: this.exportTitle,
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      this.printDocument({
        title: this.exportTitle,
        subtitle: this.monthName,
        tables: [
          {
            headers: this.dynamicTableHeaders,
            items: this.exportItems,
          },
        ],
      });
    },
  },
  beforeDestroy() {
    clearTimeout(this.copyTimer);
  },
  computed: {
    firmName() {
      const { menu, company_id: id } = this.firmReportItemInternal || {};
      return [menu, id ? `ID ${id}` : ""].filter(Boolean).join(" · ");
    },
    /** Preuzeto vide restoran i firme kojima je to dozvoljeno u podešavanjima. */
    canSeeTaken() {
      return Boolean(
        this.firmReportItemInternal?.settings?.can_see_ordered || this.$store.state.user.user?.type == "restaurant"
      );
    },
    showDifference() {
      return this.ordered_meals && this.taken_meals;
    },
    typeOptions() {
      return [
        { value: "orders", label: this.$t("orders") },
        { value: "meals", label: this.$t("report_group.meals_view") },
        { value: "foods", label: this.$t("meals_main") },
      ];
    },
    title() {
      if (this.ordered_meals) {
        return this.taken_meals
          ? this.$t("ordered_taken_foods")
          : this.$t("ordered_foods");
      }
      return this.$t("taken_foods");
    },
    exportTitle() {
      let title = this.firmReportItemInternal.menu;
      return title + " " + this.title.toLowerCase();
    },
    month() {
      return parseInt(this.date1.split("-")[1]);
    },
    year() {
      return parseInt(this.date1.split("-")[0]);
    },
    monthName() {
      const name = this.localeDate(this.date1, { month: "long", year: "numeric" });
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    /** Svi dani izabranog meseca (YYYY-MM-DD), za "Prikaži sve dane". */
    monthDates() {
      const days = moment(this.date1, MONTH).daysInMonth();
      return Array.from({ length: days }, (_, index) => `${this.date1}-${String(index + 1).padStart(2, "0")}`);
    },
    dynamicTableHeaders() {
      const headers = [
        {
          text: this.$t("day"),
          value: "day",
          sortable: false,
        },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: this.$t("total"), value: "total" },
      ];
      if (this.ordered_meals && this.taken_meals) {
        headers.push({ text: this.$t("difference"), value: "difference" });
      }
      return headers;
    },
    exportItems() {
      const totalsRow = {
        day: this.$t("total"),
        shift_one: this.totals.shift_one,
        shift_two: this.totals.shift_two,
        shift_three: this.totals.shift_three,
        shift_four: this.totals.shift_four,
        total: this.totals.total,
        difference: this.totals.total
          ? this.totals.total.quantity - this.totals.total.payed
          : 0,
      };
      return [
        ...this.processedTableData.map((item) => this.formatRowForExport(item)),
        this.formatRowForExport(totalsRow),
      ];
    },
    // Redovi po danu i smeni: "Obroci" meals/meals_picked (isto što i package_count u Korisnicima),
    // "Narudžbe" orders_count/orders_picked_count ("preuzeta narudžba" = preuzet bar jedan obrok)
    summaryRows() {
      const [ordered, picked] =
        this.type === "meals" ? ["meals", "meals_picked"] : ["orders_count", "orders_picked_count"];
      return this.summaryItems
        .map((item) => ({
          date: item.date,
          shift: item.shift,
          quantity: item[ordered] || 0,
          quantity_payed: item[picked] || 0,
        }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    },
    processedTableData() {
      const singleOrders = this.type === "foods" ? this.reportItem : this.summaryRows;
      const dates = this.allDayFirm
        ? this.monthDates
        : new Set(singleOrders.map((item) => item.date));

      return [...dates].map((date) => {
        const dateOrders = singleOrders.filter((item) => item.date === date);

        const calculateShiftOrders = (shift) => {
          const shiftOrders = dateOrders.filter((item) => item.shift === shift);
          return {
            quantity: shiftOrders.reduce((sum, item) => sum + item.quantity, 0),
            payed: shiftOrders.reduce((sum, item) => sum + item.quantity_payed, 0),
          };
        };

        const totalQuantity = dateOrders.reduce((sum, item) => sum + item.quantity, 0);
        const totalPayed = dateOrders.reduce((sum, item) => sum + item.quantity_payed, 0);

        return {
          date,
          day: this.localeDate(date, { day: "2-digit", month: "short", year: "numeric" }),
          shift_one: calculateShiftOrders(1),
          shift_two: calculateShiftOrders(2),
          shift_three: calculateShiftOrders(3),
          shift_four: calculateShiftOrders(4),
          total: {
            quantity: totalQuantity,
            payed: totalPayed,
          },
          difference: totalQuantity - totalPayed,
        };
      });
    },
    totals() {
      const tableData = this.processedTableData;
      return {
        shift_one: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_one.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_one.payed, 0),
        },
        shift_two: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_two.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_two.payed, 0),
        },
        shift_three: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_three.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_three.payed, 0),
        },
        shift_four: {
          quantity: tableData.reduce((sum, item) => sum + item.shift_four.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.shift_four.payed, 0),
        },
        total: {
          quantity: tableData.reduce((sum, item) => sum + item.total.quantity, 0),
          payed: tableData.reduce((sum, item) => sum + item.total.payed, 0),
        },
      };
    },
    /** Smene 1–3 uvek, četvrta samo kad u njoj ima nešto u izabranom mesecu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) => shift.id < 4 || this.totals.shift_four.quantity > 0 || this.totals.shift_four.payed > 0
      );
    },
    /** Redovi tabele na ekranu: dan sa danom u nedelji i ćelije po smeni. */
    rows() {
      return this.processedTableData.map((item) => {
        const cells = { total: this.cellOf(item.total) };
        SHIFTS.forEach((shift) => {
          cells[shift.key] = this.cellOf(item[shift.key]);
        });
        return {
          key: item.date,
          // godina je u nazivu meseca; za kopiranje se koristi pun datum (item.day)
          day: this.localeDate(item.date, { weekday: "short", day: "2-digit", month: "short" }),
          copyDay: item.day,
          cells,
          difference: item.difference,
        };
      });
    },
    columns() {
      return [
        { key: "day", label: this.$t("day"), name: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end" })),
        { key: "total", label: this.$t("total"), align: "end", num: true },
        ...(this.showDifference
          ? [
              {
                key: "difference",
                label: this.$t("difference"),
                align: "end",
                num: true,
                zero: true,
                cellClass: (row) => ({ "is-alert": row.difference > 0 }),
              },
            ]
          : []),
      ];
    },
    /** Zbirni red (tekst po koloni). */
    footTotals() {
      const foot = { total: this.cellOf(this.totals.total).text, difference: this.totals.total.quantity - this.totals.total.payed };
      this.visibleShifts.forEach((shift) => {
        const cell = this.cellOf(this.totals[shift.key]);
        foot[shift.key] = cell.empty ? "–" : cell.text;
      });
      return foot;
    },
  },
  watch: {
    type(type, previous) {
      // "Obroci" i "Narudžbe" su isti odgovor (group=orders), nov zahtev treba samo za "Jela"
      if (this.model && (type === "foods") !== (previous === "foods")) {
        this.getCompanyMonthlyOrders();
      }
    },
    model: {
      handler(newVal) {
        if (newVal) {
          // firmReportItemInternal is now primarily managed by firmReportItem watcher
          this.getCompanyMonthlyOrders();
        } else {
          this.rawFirmDataByDate = {};
          // Reset other relevant states if necessary
        }
      },
    },
    firmReportItem: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.firmReportItemInternal = structuredClone(newVal);
        } else {
          this.firmReportItemInternal = {};
        }
        if (this.model) {
          // If dialog is open, refresh data
          this.getCompanyMonthlyOrders();
        }
      },
      deep: true,
      immediate: true, // Initialize firmReportItemInternal on component load
    },
    date1: {
      handler() {
        if (this.model) {
          // Only fetch if dialog is open
          this.getCompanyMonthlyOrders();
        }
      },
    },
  },
};
</script>

<style lang="scss">
.rd.gm-month {
  min-height: 100vh;

  .gm-month__bar {
    position: sticky;
    top: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 24px;
    background: var(--rm-surface);
    border-bottom: 1px solid var(--rm-border);
  }

  .gm-month__title {
    flex: 1;
    min-width: 0;
    display: grid;

    h2 {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.25;
    }

    span {
      font-size: 13px;
      overflow-wrap: anywhere;
    }
  }

  .gm-month__body {
    max-width: 1120px;
    margin: 0 auto;
    padding: 20px 24px 48px;
    display: grid;
    gap: 16px;
  }

  .gm-month__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(110px, 180px));
    gap: 12px;
  }

  .gm-month__tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
  }

  .gm-month__nav {
    min-width: 240px;
  }

  // "131 / 125" ostaje u jednom redu; uska tabela se pomera vodoravno
  .rd-tbl th,
  .rd-tbl td {
    white-space: nowrap;
  }

  &.ui-compact {
    .gm-month__bar {
      padding: 10px 12px;
    }

    .gm-month__body {
      padding: 16px 12px 40px;
    }

    .gm-month__label {
      display: none;
    }

    .gm-month__stats {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .gm-month__nav {
      width: 100%;
    }
  }
}
</style>
